import type { Availability, ChatStatus } from "@beacon/core/chat"
import { maxStatusText } from "@beacon/core/chat/rules"
import { useQueryClient } from "@tanstack/react-query"
import { Loader2Icon, XIcon } from "lucide-react"
import { useId, useRef, useState, type FormEvent } from "react"
import { toast } from "sonner"
import { refusalOf } from "@/administration/client.ts"
import { Kbd, useViewerZone } from "@/design-system/index.ts"
import { EmojiButton } from "@/emoji/emoji-picker.tsx"
import { Button } from "@/primitives/button.tsx"
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/primitives/dialog.tsx"
import { Field, FieldLabel } from "@/primitives/field.tsx"
import { InputGroup, InputGroupAddon, InputGroupInput } from "@/primitives/input-group.tsx"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/primitives/select.tsx"
import { useShortcut } from "@/shortcuts/use-shortcut.ts"
import { describeChatRefusal } from "./describe.ts"
import {
  clearAfterChoices,
  clearStatus,
  describeUntil,
  presenceKeys,
  setStatus,
  statusShown,
  touchesMember,
  untilOf,
  type ClearAfter,
} from "./presence.ts"

/*
 * Set a status: what the Member says of themselves to everyone in Chat, an emoji and a few words,
 * and when it clears by itself. A few common ones fill it in a tap. A status already set opens as
 * it is, keeping the time it clears at unless another is chosen, and can be cleared here.
 */

/** Common statuses, each with the time it usually clears after. */
const suggestions: readonly { emoji: string; text: string; clears: ClearAfter }[] = [
  { emoji: "📅", text: "In a meeting", clears: "1h" },
  { emoji: "🚌", text: "Commuting", clears: "30m" },
  { emoji: "🎧", text: "Focusing", clears: "4h" },
  { emoji: "🤒", text: "Off sick", clears: "today" },
  { emoji: "🌴", text: "On holiday", clears: "never" },
]

type Choice = ClearAfter | "keep"

export function StatusDialog({
  open,
  onOpenChange,
  me,
  current,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  me: string
  current: ChatStatus | null
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="sm">
        {/* A fresh form each time it opens, from the status as it is then. */}
        {open && <StatusForm me={me} current={current} onDone={() => onOpenChange(false)} />}
      </DialogContent>
    </Dialog>
  )
}

function StatusForm({
  me,
  current,
  onDone,
}: {
  me: string
  current: ChatStatus | null
  onDone: () => void
}) {
  const queryClient = useQueryClient()
  const zone = useViewerZone()
  const shown = statusShown(current, Date.now())
  const [emoji, setEmoji] = useState<string | null>(shown?.emoji ?? null)
  const [text, setText] = useState(shown?.text ?? "")
  const [choice, setChoice] = useState<Choice>(shown?.until ? "keep" : "never")
  const [pending, setPending] = useState<"save" | "clear" | null>(null)
  const words = useRef<HTMLInputElement>(null)
  const form = useRef<HTMLFormElement>(null)
  const field = useId()
  const clears = useId()
  const empty = !emoji && text.trim() === ""

  const choices = [
    ...(shown?.until
      ? [{ value: "keep" as const, label: describeUntil(shown.until, Date.now(), zone) }]
      : []),
    ...clearAfterChoices,
  ]

  function settled(next: Availability) {
    queryClient.setQueryData(presenceKeys.availability, next)
    void queryClient.invalidateQueries({
      predicate: ({ queryKey }) => touchesMember(queryKey, me),
    })
  }

  async function run(what: "save" | "clear") {
    setPending(what)
    try {
      settled(
        what === "clear"
          ? await clearStatus()
          : await setStatus({
              emoji,
              text: text.trim() || null,
              until: choice === "keep" ? (shown?.until ?? null) : untilOf(choice, Date.now()),
            }),
      )
      toast.success(what === "clear" ? "Status cleared" : "Status set")
      onDone()
    } catch (error) {
      toast.error(describeChatRefusal(refusalOf(error)))
      setPending(null)
    }
  }

  function submit(event?: FormEvent) {
    event?.preventDefault()
    if (!empty && !pending) void run("save")
  }
  useShortcut("submit", () => submit(), { within: form })

  return (
    <form ref={form} onSubmit={submit}>
      <DialogHeader>
        <DialogTitle>{shown ? "Edit your status" : "Set a status"}</DialogTitle>
        <DialogDescription>Everyone in Chat sees it beside your name.</DialogDescription>
      </DialogHeader>
      <DialogBody className="space-y-4">
        <Field>
          <FieldLabel htmlFor={field}>Status</FieldLabel>
          <InputGroup>
            <InputGroupAddon>
              {emoji ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-xs"
                  aria-label={`Remove emoji ${emoji}`}
                  onClick={() => setEmoji(null)}
                  className="text-ui"
                >
                  {emoji}
                </Button>
              ) : (
                <EmojiButton
                  label="Choose an emoji"
                  onPick={setEmoji}
                  focusAfter={() => words.current?.focus()}
                />
              )}
            </InputGroupAddon>
            <InputGroupInput
              id={field}
              ref={words}
              autoFocus
              value={text}
              maxLength={maxStatusText}
              placeholder="What's your status?"
              onChange={(event) => setText(event.target.value)}
            />
            {!empty && (
              <InputGroupAddon align="inline-end">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-xs"
                  aria-label="Empty the status"
                  onClick={() => {
                    setEmoji(null)
                    setText("")
                    words.current?.focus()
                  }}
                >
                  <XIcon />
                </Button>
              </InputGroupAddon>
            )}
          </InputGroup>
        </Field>
        {empty && (
          <ul aria-label="Suggestions" className="-mx-2 space-y-0.5">
            {suggestions.map((suggestion) => (
              <li key={suggestion.text}>
                <button
                  type="button"
                  onClick={() => {
                    setEmoji(suggestion.emoji)
                    setText(suggestion.text)
                    setChoice(suggestion.clears)
                    words.current?.focus()
                  }}
                  className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-ui hover:bg-accent focus-visible:bg-accent focus-visible:outline-none"
                >
                  <span aria-hidden>{suggestion.emoji}</span>
                  <span>{suggestion.text}</span>
                  <span className="ml-auto text-xs text-subtle-foreground">
                    {clearAfterChoices.find(({ value }) => value === suggestion.clears)?.label}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
        <Field>
          <FieldLabel htmlFor={clears}>Clear after</FieldLabel>
          <Select
            items={choices}
            value={choice}
            onValueChange={(value) => value && setChoice(value as Choice)}
          >
            <SelectTrigger id={clears} className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {choices.map(({ value, label }) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
      </DialogBody>
      <DialogFooter>
        {shown && (
          <Button
            type="button"
            variant="ghost"
            className="mr-auto"
            disabled={pending !== null}
            onClick={() => void run("clear")}
          >
            {pending === "clear" && (
              <Loader2Icon data-icon="inline-start" className="animate-spin" />
            )}
            Clear status
          </Button>
        )}
        <DialogClose render={<Button variant="outline" />} disabled={pending !== null}>
          Cancel
        </DialogClose>
        <Button type="submit" disabled={empty || pending !== null}>
          {pending === "save" && <Loader2Icon data-icon="inline-start" className="animate-spin" />}
          Save
          {!pending && <Kbd shortcut="submit" />}
        </Button>
      </DialogFooter>
    </form>
  )
}
